import * as THREE from 'three';
import { createSeededRandom } from '../utils/random';
import { SKY_DOME_NAME } from './SkySystem';
import { OCEAN_WATER_NAME } from './OceanSystem';

/**
 * Client-side storm visuals: three rain layers (near wind-slanted streaks, a
 * denser mid sheet and a distant mist bank), pooled deck/sea splash sprites,
 * lightning flashes and fog density. No per-frame allocation — every scratch
 * colour, the instancing dummy, the sprite pools and the drop buffers are
 * created once and reused.
 *
 * - Near rain is a sparse `InstancedMesh` of long, fast, bright streaks that
 *   fall close around the camera; mid rain is a denser, dimmer, shorter sheet
 *   further out. Per-drop length and brightness vary so the downpour never
 *   reads as uniform scratches.
 * - Distant mist is a ring of large soft radial sprites hugging the horizon.
 * - Splash sprites pop on the sea surface and on the ship deck, expanding and
 *   fading with a short lifetime, pushed sideways by the wind.
 * - A lightning strike (`lightning: true`) fires a decaying flash that brightens
 *   the background, the fog colour, the cloud deck, the rain, the mist, the
 *   splashes and the sea, plus an ambient flash light.
 * - Fog density is driven by `fog`/`storm`: near/far pull in and the fog colour
 *   deepens toward a storm tone.
 * - The ocean's sun/hemisphere lights and the water's specular sun are re-tinted
 *   cold and dimmed as `storm` rises.
 */
export interface WeatherLike {
  storm: number;
  rain: number;
  cloud: number;
  fog: number;
  gust: number;
  lightning: boolean;
}

const RAIN_SEED = 0x4a1d;
const MIST_SEED = 0x77b3;
const SPLASH_PLACE_SEED = 0x2c5a;
const SPLASH_POOL_SEED = 0x3f21;

// ── Near rain ────────────────────────────────────────────────────────
const NEAR_RAIN_COUNT_DESKTOP = 900;
const NEAR_RAIN_COUNT_MOBILE = 360;
const NEAR_RAIN_RADIUS_DESKTOP = 26;
const NEAR_RAIN_RADIUS_MOBILE = 20;
const NEAR_RAIN_TOP = 46;
const NEAR_RAIN_TOP_JITTER = 12;
const NEAR_RAIN_FLOOR = 0.4;
const NEAR_RAIN_FALL_SPEED = 46;
const NEAR_RAIN_DRIFT = 20;
const NEAR_RAIN_SLANT = 0.42;
const NEAR_RAIN_WIDTH = 0.07;
const NEAR_RAIN_MIN_LENGTH = 2.2;
const NEAR_RAIN_MAX_LENGTH = 4.2;
const NEAR_RAIN_SPEED_MIN = 0.85;
const NEAR_RAIN_SPEED_MAX = 1.35;
const NEAR_RAIN_OPACITY_DESKTOP = 0.5;
const NEAR_RAIN_OPACITY_MOBILE = 0.36;
const NEAR_RAIN_COLOR = 0xbcd2ea;
const NEAR_RAIN_TILT_JITTER = 0.07;
const NEAR_RAIN_WIDTH_MIN = 0.7;
const NEAR_RAIN_WIDTH_MAX = 1.3;
const NEAR_RAIN_BRIGHTNESS_MIN = 0.45;

// ── Mid rain ─────────────────────────────────────────────────────────
const MID_RAIN_COUNT_DESKTOP = 1500;
const MID_RAIN_COUNT_MOBILE = 520;
const MID_RAIN_RADIUS_DESKTOP = 58;
const MID_RAIN_RADIUS_MOBILE = 42;
const MID_RAIN_TOP = 40;
const MID_RAIN_TOP_JITTER = 10;
const MID_RAIN_FLOOR = 0.4;
const MID_RAIN_FALL_SPEED = 34;
const MID_RAIN_DRIFT = 14;
const MID_RAIN_SLANT = 0.26;
const MID_RAIN_WIDTH = 0.05;
const MID_RAIN_MIN_LENGTH = 1.4;
const MID_RAIN_MAX_LENGTH = 2.6;
const MID_RAIN_SPEED_MIN = 0.7;
const MID_RAIN_SPEED_MAX = 1.15;
const MID_RAIN_OPACITY_DESKTOP = 0.3;
const MID_RAIN_OPACITY_MOBILE = 0.2;
const MID_RAIN_COLOR = 0x9fb6d0;
const MID_RAIN_TILT_JITTER = 0.05;
const MID_RAIN_WIDTH_MIN = 0.6;
const MID_RAIN_WIDTH_MAX = 1.1;
const MID_RAIN_BRIGHTNESS_MIN = 0.3;

// ── Distant mist ─────────────────────────────────────────────────────
const MIST_COUNT_DESKTOP = 10;
const MIST_COUNT_MOBILE = 6;
const MIST_RADIUS_DESKTOP = 60;
const MIST_RADIUS_MOBILE = 44;
const MIST_MIN_SCALE = 30;
const MIST_MAX_SCALE = 52;
const MIST_OPACITY = 0.1;
const MIST_COLOR = 0x8fa4b4;
const MIST_DRIFT = 4;

// ── Deck/sea splash sprites ──────────────────────────────────────────
const SPLASH_COUNT_DESKTOP = 40;
const SPLASH_COUNT_MOBILE = 18;
const SPLASH_RADIUS_DESKTOP = 40;
const SPLASH_RADIUS_MOBILE = 30;
const SPLASH_INTERVAL = 0.07;
const DECK_SPLASH_INTERVAL = 0.16;
const SPLASH_MIN_SCALE = 0.55;
const SPLASH_MAX_SCALE = 1.5;
const SPLASH_MIN_LIFE = 0.5;
const SPLASH_MAX_LIFE = 0.9;
const SPLASH_OPACITY = 0.5;
const SPLASH_COLOR = 0xbfd4ea;
/** Deck surface height in the ship's local frame (matches `Game.DECK_HEIGHT`). */
const DECK_HEIGHT = 1.9;

/** Seconds for a lightning flash to fade out. */
const FLASH_DECAY = 0.34;
const FLASH_LIGHT_INTENSITY = 1.6;

/** Storm lighting re-tint: cold steel-blue sun, dimmer by this fraction. */
const DIR_DIM_FACTOR = 0.55;
const DIR_TINT = 0x86a4c6;
const HEMI_DIM_FACTOR = 0.35;
const HEMI_SKY_TINT = 0x5a7088;
const HEMI_GROUND_TINT = 0x0e1824;
/** Water specular-sun tint so the sea glint turns cold in a storm. */
const WATER_SUN_TINT = 0x8fa8c8;

/** How far fog near/far pull in at full storm (fraction of the base range). */
const FOG_NEAR_PULL = 0.35;
const FOG_FAR_PULL = 0.55;
const STORM_FOG_COLOR = 0x16202c;
const STORM_BG_COLOR = 0x0e141c;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

function clampSigned(value: number): number {
  return Math.min(1, Math.max(-1, value));
}

/** Soft radial falloff (white centre fading out) for mist/splash sprites. */
function createRadialSpriteTexture(size: number): THREE.DataTexture {
  const data = new Uint8Array(size * size * 4);
  const half = size * 0.5;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const dx = (x + 0.5 - half) / half;
      const dy = (y + 0.5 - half) / half;
      const distance = Math.min(1, Math.sqrt(dx * dx + dy * dy));
      const alpha = Math.pow(Math.max(0, 1 - distance), 2.2);
      const index = (y * size + x) * 4;
      data[index] = 255;
      data[index + 1] = 255;
      data[index + 2] = 255;
      data[index + 3] = Math.round(alpha * 255);
    }
  }
  const texture = new THREE.DataTexture(data, size, size, THREE.RGBAFormat, THREE.UnsignedByteType);
  texture.needsUpdate = true;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.generateMipmaps = false;
  return texture;
}

interface RainLayerConfig {
  count: number;
  radius: number;
  minRadius: number;
  top: number;
  topJitter: number;
  floor: number;
  fallSpeed: number;
  drift: number;
  slant: number;
  width: number;
  minLength: number;
  maxLength: number;
  speedMin: number;
  speedMax: number;
  opacity: number;
  color: number;
  tiltJitter: number;
  widthMin: number;
  widthMax: number;
  brightnessMin: number;
}

/** One pooled `InstancedMesh` of wind-slanted rain streaks. */
class RainLayer {
  private readonly geometry: THREE.BoxGeometry;
  private readonly material: THREE.MeshBasicMaterial;
  readonly mesh: THREE.InstancedMesh;
  private readonly dummy = new THREE.Object3D();
  private readonly config: RainLayerConfig;
  private readonly rng: () => number;
  private readonly x: Float32Array;
  private readonly y: Float32Array;
  private readonly z: Float32Array;
  private readonly speed: Float32Array;
  private readonly length: Float32Array;
  private readonly tilt: Float32Array;
  private readonly width: Float32Array;
  private readonly baseColor = new THREE.Color();
  private readonly litColor = new THREE.Color(0xffffff);
  private readonly colorScratch = new THREE.Color();

  constructor(scene: THREE.Scene, config: RainLayerConfig, rng: () => number) {
    this.config = config;
    this.rng = rng;
    this.baseColor.setHex(config.color);

    this.geometry = new THREE.BoxGeometry(config.width, 1, config.width);
    this.material = new THREE.MeshBasicMaterial({
      color: this.baseColor,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      fog: true,
    });
    this.mesh = new THREE.InstancedMesh(this.geometry, this.material, config.count);
    this.mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.mesh.frustumCulled = false;
    this.mesh.visible = false;
    this.mesh.name = 'storm-rain';
    scene.add(this.mesh);

    this.x = new Float32Array(config.count);
    this.y = new Float32Array(config.count);
    this.z = new Float32Array(config.count);
    this.speed = new Float32Array(config.count);
    this.length = new Float32Array(config.count);
    this.tilt = new Float32Array(config.count);
    this.width = new Float32Array(config.count);

    const brightness = new THREE.Color();
    for (let i = 0; i < config.count; i += 1) {
      this.respawn(i, 0, 0, 0);
      this.speed[i] = config.speedMin + rng() * (config.speedMax - config.speedMin);
      this.length[i] = config.minLength + rng() * (config.maxLength - config.minLength);
      // Per-drop slant offset, width and brightness (spread below 1) vary so the
      // sheet never reads as a uniform grid of identical vertical scratches.
      this.tilt[i] = (rng() * 2 - 1) * config.tiltJitter;
      this.width[i] = config.widthMin + rng() * (config.widthMax - config.widthMin);
      brightness.setScalar(config.brightnessMin + rng() * (1 - config.brightnessMin));
      this.mesh.setColorAt(i, brightness);
    }
    if (this.mesh.instanceColor) this.mesh.instanceColor.needsUpdate = true;
  }

  update(
    dt: number,
    rain: number,
    gust: number,
    flash: number,
    camX: number,
    camY: number,
    camZ: number,
  ): void {
    const cfg = this.config;
    const drift = gust * cfg.drift;

    // Keep the pool falling even while invisible so the field stays populated.
    for (let i = 0; i < cfg.count; i += 1) {
      this.y[i] -= cfg.fallSpeed * this.speed[i] * dt;
      this.x[i] += drift * dt;
      if (this.y[i] < cfg.floor) this.respawn(i, camX, camY, camZ);
    }

    if (rain <= 0.01) {
      this.mesh.visible = false;
      return;
    }

    this.mesh.visible = true;
    this.material.opacity = cfg.opacity * rain * (1 + flash * 0.7);
    this.colorScratch.copy(this.baseColor).lerp(this.litColor, flash * 0.65);
    this.material.color.copy(this.colorScratch);

    for (let i = 0; i < cfg.count; i += 1) {
      const slant = gust * cfg.slant + this.tilt[i];
      this.dummy.position.set(this.x[i], this.y[i], this.z[i]);
      this.dummy.rotation.set(0, 0, slant);
      this.dummy.scale.set(this.width[i], this.length[i], this.width[i]);
      this.dummy.updateMatrix();
      this.mesh.setMatrixAt(i, this.dummy.matrix);
    }
    this.mesh.instanceMatrix.needsUpdate = true;
  }

  dispose(): void {
    this.mesh.removeFromParent();
    this.mesh.dispose();
    this.geometry.dispose();
    this.material.dispose();
  }

  /** Place a drop at a random point above the camera, inside the rain disc. */
  private respawn(index: number, camX: number, camY: number, camZ: number): void {
    const angle = this.rng() * Math.PI * 2;
    // Uniform annulus [minRadius, radius]: near rain fills the camera disc and
    // mid rain fills the ring beyond it, so the two depth layers don't overlap.
    const minR = this.config.minRadius;
    const maxR = this.config.radius;
    const radius = Math.sqrt(minR * minR + this.rng() * (maxR * maxR - minR * minR));
    this.x[index] = camX + Math.cos(angle) * radius;
    this.z[index] = camZ + Math.sin(angle) * radius;
    this.y[index] = camY + this.config.top + this.rng() * this.config.topJitter;
  }
}

interface MistSprite {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  x: number;
  y: number;
  z: number;
  scale: number;
  opacity: number;
  driftSpeed: number;
}

/** A ring of large soft billboards that reads as a distant wet haze bank. */
class MistField {
  private readonly sprites: MistSprite[] = [];
  private readonly texture: THREE.DataTexture;
  private readonly count: number;
  private readonly radius: number;
  private readonly baseColor = new THREE.Color(MIST_COLOR);
  private readonly litColor = new THREE.Color(0xffffff);
  private readonly colorScratch = new THREE.Color();

  constructor(scene: THREE.Scene, mobile: boolean, rng: () => number) {
    this.count = mobile ? MIST_COUNT_MOBILE : MIST_COUNT_DESKTOP;
    this.radius = mobile ? MIST_RADIUS_MOBILE : MIST_RADIUS_DESKTOP;
    this.texture = createRadialSpriteTexture(64);

    for (let i = 0; i < this.count; i += 1) {
      const material = new THREE.SpriteMaterial({
        map: this.texture,
        color: this.baseColor,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        fog: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.visible = false;
      sprite.name = 'storm-mist';
      scene.add(sprite);

      const angle = rng() * Math.PI * 2;
      const distance = (0.5 + rng() * 0.5) * this.radius;
      this.sprites.push({
        sprite,
        material,
        x: Math.cos(angle) * distance,
        y: 1.5 + rng() * 7,
        z: Math.sin(angle) * distance,
        scale: MIST_MIN_SCALE + rng() * (MIST_MAX_SCALE - MIST_MIN_SCALE),
        opacity: 0.6 + rng() * 0.4,
        driftSpeed: 0.7 + rng() * 0.6,
      });
    }
  }

  update(dt: number, rain: number, gust: number, flash: number, camX: number, camZ: number): void {
    if (rain <= 0.02) {
      for (const mist of this.sprites) mist.sprite.visible = false;
      return;
    }

    for (const mist of this.sprites) {
      mist.sprite.visible = true;
      mist.x += gust * MIST_DRIFT * mist.driftSpeed * dt;

      // Wrap the bank around the camera so it never drifts out of range.
      const dx = mist.x - camX;
      const dz = mist.z - camZ;
      if (Math.hypot(dx, dz) > this.radius) {
        mist.x = camX - dx * 0.85;
        mist.z = camZ - dz * 0.85;
      }

      mist.sprite.position.set(mist.x, mist.y, mist.z);
      mist.sprite.scale.set(mist.scale, mist.scale, 1);
      this.colorScratch.copy(this.baseColor).lerp(this.litColor, flash * 0.5);
      mist.material.color.copy(this.colorScratch);
      mist.material.opacity = MIST_OPACITY * mist.opacity * rain * (1 + flash * 0.6);
    }
  }

  dispose(): void {
    for (const mist of this.sprites) {
      mist.sprite.removeFromParent();
      mist.material.dispose();
    }
    this.texture.dispose();
  }
}

interface SplashState {
  sprite: THREE.Sprite;
  material: THREE.SpriteMaterial;
  active: boolean;
  age: number;
  life: number;
  scale: number;
  opacity: number;
  vx: number;
  vy: number;
  vz: number;
}

/** Pooled billboard sprites for short-lived deck and sea surface splashes. */
class SplashPool {
  private readonly states: SplashState[] = [];
  private readonly texture: THREE.DataTexture;
  private readonly count: number;
  readonly radius: number;
  private cursor = 0;
  private readonly rng: () => number;
  private readonly baseColor = new THREE.Color(SPLASH_COLOR);
  private readonly litColor = new THREE.Color(0xffffff);
  private readonly colorScratch = new THREE.Color();

  constructor(scene: THREE.Scene, mobile: boolean, rng: () => number) {
    this.count = mobile ? SPLASH_COUNT_MOBILE : SPLASH_COUNT_DESKTOP;
    this.radius = mobile ? SPLASH_RADIUS_MOBILE : SPLASH_RADIUS_DESKTOP;
    this.rng = rng;
    this.texture = createRadialSpriteTexture(48);

    for (let i = 0; i < this.count; i += 1) {
      const material = new THREE.SpriteMaterial({
        map: this.texture,
        color: this.baseColor,
        transparent: true,
        opacity: 0,
        depthWrite: false,
        fog: false,
      });
      const sprite = new THREE.Sprite(material);
      sprite.visible = false;
      sprite.name = 'storm-splash';
      scene.add(sprite);
      this.states.push({
        sprite,
        material,
        active: false,
        age: 0,
        life: 1,
        scale: 1,
        opacity: 1,
        vx: 0,
        vy: 0,
        vz: 0,
      });
    }
  }

  spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number): void {
    const state = this.states[this.cursor];
    this.cursor = (this.cursor + 1) % this.count;
    state.active = true;
    state.age = 0;
    state.life = SPLASH_MIN_LIFE + this.rng() * (SPLASH_MAX_LIFE - SPLASH_MIN_LIFE);
    state.scale = SPLASH_MIN_SCALE + this.rng() * (SPLASH_MAX_SCALE - SPLASH_MIN_SCALE);
    state.opacity = 0.7 + this.rng() * 0.3;
    state.vx = vx;
    state.vy = vy;
    state.vz = vz;
    state.sprite.position.set(x, y, z);
    state.sprite.visible = true;
  }

  update(dt: number, rain: number, flash: number): void {
    if (rain <= 0.02) {
      for (const state of this.states) {
        state.active = false;
        state.sprite.visible = false;
      }
      return;
    }

    for (const state of this.states) {
      if (!state.active) {
        state.sprite.visible = false;
        continue;
      }
      state.age += dt;
      if (state.age >= state.life) {
        state.active = false;
        state.sprite.visible = false;
        continue;
      }

      const t = state.age / state.life;
      state.sprite.position.x += state.vx * dt;
      state.sprite.position.y += state.vy * dt;
      state.sprite.position.z += state.vz * dt;
      const scale = state.scale * (0.4 + t * 1.3);
      state.sprite.scale.set(scale, scale, 1);
      this.colorScratch.copy(this.baseColor).lerp(this.litColor, flash * 0.55);
      state.material.color.copy(this.colorScratch);
      state.material.opacity = SPLASH_OPACITY * state.opacity * (1 - t) * rain * (1 + flash * 0.5);
      state.sprite.visible = true;
    }
  }

  dispose(): void {
    for (const state of this.states) {
      state.sprite.removeFromParent();
      state.material.dispose();
    }
    this.texture.dispose();
  }
}

export class StormFx {
  private readonly scene: THREE.Scene;
  private readonly camera: THREE.PerspectiveCamera;

  private readonly nearRain: RainLayer;
  private readonly midRain: RainLayer;
  private readonly mist: MistField;
  private readonly splashes: SplashPool;
  private readonly splashRng: () => number;
  private readonly splashRadius: number;

  private readonly flashLight: THREE.AmbientLight;
  private flashEnergy = 0;
  private seaSplashTimer = 0;
  private deckSplashTimer = 0;

  /** Ocean lights owned by `OceanSystem`, re-tinted/dimmed in place. */
  private readonly sunLight: THREE.DirectionalLight | null;
  private readonly hemisphereLight: THREE.HemisphereLight | null;

  /** Sky-dome and ocean-water uniforms driven by the storm (found lazily). */
  private readonly skyUniforms: Record<string, THREE.IUniform> | null;
  private readonly waterUniforms: Record<string, THREE.IUniform> | null;

  /** Player ship group, used to anchor deck splashes. */
  private readonly shipGroup: THREE.Object3D | null;
  private readonly shipWorldPos = new THREE.Vector3();
  private readonly shipQuat = new THREE.Quaternion();
  private readonly shipLocal = new THREE.Vector3();
  private readonly shipWorldOffset = new THREE.Vector3();

  // Preallocated colour scratch buffers (reused every frame, never re-created).
  private readonly fogColor = new THREE.Color();
  private readonly bgColor = new THREE.Color();
  private readonly baseFogColor = new THREE.Color();
  private readonly baseBackground = new THREE.Color();
  private readonly stormFogColor = new THREE.Color(STORM_FOG_COLOR);
  private readonly stormBgColor = new THREE.Color(STORM_BG_COLOR);
  private readonly flashWhite = new THREE.Color(0xffffff);

  // Storm lighting re-tint scratch state.
  private readonly baseSunColor = new THREE.Color();
  private readonly coldSunColor = new THREE.Color(DIR_TINT);
  private readonly sunColorScratch = new THREE.Color();
  private baseSunIntensity = 0;
  private sunBaseReady = false;
  private readonly baseHemiSky = new THREE.Color();
  private readonly baseHemiGround = new THREE.Color();
  private readonly coldHemiSky = new THREE.Color(HEMI_SKY_TINT);
  private readonly coldHemiGround = new THREE.Color(HEMI_GROUND_TINT);
  private readonly hemiSkyScratch = new THREE.Color();
  private readonly hemiGroundScratch = new THREE.Color();
  private baseHemiIntensity = 0;
  private hemiBaseReady = false;

  // Water specular-sun scratch state.
  private readonly baseWaterSunColor = new THREE.Color();
  private readonly coldWaterSunColor = new THREE.Color(WATER_SUN_TINT);
  private readonly waterSunScratch = new THREE.Color();
  private waterSunBaseReady = false;

  private baseFogNear = 0;
  private baseFogFar = 0;
  private fogBaseReady = false;
  private bgBaseReady = false;

  constructor(scene: THREE.Scene, camera: THREE.PerspectiveCamera, mobile: boolean) {
    this.scene = scene;
    this.camera = camera;

    const rainRng = createSeededRandom(RAIN_SEED);
    this.nearRain = new RainLayer(scene, makeNearRainConfig(mobile), rainRng);
    this.midRain = new RainLayer(scene, makeMidRainConfig(mobile), rainRng);
    this.mist = new MistField(scene, mobile, createSeededRandom(MIST_SEED));
    this.splashRng = createSeededRandom(SPLASH_PLACE_SEED);
    this.splashes = new SplashPool(scene, mobile, createSeededRandom(SPLASH_POOL_SEED));
    this.splashRadius = this.splashes.radius;

    this.flashLight = new THREE.AmbientLight(0xffffff, 0);
    this.flashLight.name = 'storm-flash-light';
    this.scene.add(this.flashLight);

    // The ocean's hemisphere + directional sun are created before this system;
    // find them once so the storm can re-tint and dim them in place.
    let sunLight: THREE.DirectionalLight | null = null;
    let hemisphereLight: THREE.HemisphereLight | null = null;
    this.scene.traverse((object) => {
      if (!sunLight && object instanceof THREE.DirectionalLight) sunLight = object;
      if (!hemisphereLight && object instanceof THREE.HemisphereLight) hemisphereLight = object;
    });
    this.sunLight = sunLight;
    this.hemisphereLight = hemisphereLight;

    // The sky dome and ocean water are constructed before `StormFx`, so their
    // materials can be found here and driven without owning either system.
    const sky = this.scene.getObjectByName(SKY_DOME_NAME);
    this.skyUniforms =
      sky instanceof THREE.Mesh && (sky.material as THREE.Material | null)?.type === 'ShaderMaterial'
        ? (sky.material as THREE.ShaderMaterial).uniforms
        : null;
    const water = this.scene.getObjectByName(OCEAN_WATER_NAME);
    this.waterUniforms =
      water instanceof THREE.Mesh && (water.material as THREE.Material | null)?.type === 'ShaderMaterial'
        ? (water.material as THREE.ShaderMaterial).uniforms
        : null;

    // The player ship is the first 'ship' group added to the scene; deck
    // splashes are anchored to its local deck frame.
    this.shipGroup = this.scene.getObjectByName('ship') ?? null;
  }

  update(delta: number, weather: WeatherLike): void {
    const dt = Math.min(Math.max(delta, 0), 0.1);
    const rain = clamp01(Number.isFinite(weather.rain) ? weather.rain : 0);
    const fogAmount = clamp01(Number.isFinite(weather.fog) ? weather.fog : 0);
    const stormAmount = clamp01(Number.isFinite(weather.storm) ? weather.storm : 0);
    const cloudAmount = clamp01(Number.isFinite(weather.cloud) ? weather.cloud : 0);
    const gust = clampSigned(Number.isFinite(weather.gust) ? weather.gust : 0);

    if (weather.lightning) {
      this.flashEnergy = 1;
    } else {
      this.flashEnergy = Math.max(0, this.flashEnergy - dt / FLASH_DECAY);
    }
    const flash = this.flashEnergy * this.flashEnergy;

    const cam = this.camera.position;
    const camX = cam.x;
    const camY = cam.y;
    const camZ = cam.z;

    this.nearRain.update(dt, rain, gust, flash, camX, camY, camZ);
    this.midRain.update(dt, rain, gust, flash, camX, camY, camZ);
    this.mist.update(dt, rain, gust, flash, camX, camZ);
    this.updateSplashes(dt, rain, gust, flash, camX, camZ);

    this.updateFog(fogAmount, stormAmount, flash);
    this.updateBackground(stormAmount, cloudAmount, flash);
    this.updateLighting(stormAmount, flash);
    this.updateWaterSun(stormAmount, flash);
    this.flashLight.intensity = flash * FLASH_LIGHT_INTENSITY;

    // Lightning + storm also light the sea surface and the cloud deck.
    if (this.skyUniforms) {
      this.skyUniforms.uStorm.value = stormAmount;
      this.skyUniforms.uFlash.value = flash;
    }
    if (this.waterUniforms) {
      this.waterUniforms.uFlash.value = flash;
    }
  }

  dispose(): void {
    this.nearRain.dispose();
    this.midRain.dispose();
    this.mist.dispose();
    this.splashes.dispose();
    this.scene.remove(this.flashLight);

    // Restore the ocean lights and water specular sun to their calm state.
    if (this.sunLight && this.sunBaseReady) {
      this.sunLight.color.copy(this.baseSunColor);
      this.sunLight.intensity = this.baseSunIntensity;
    }
    if (this.hemisphereLight && this.hemiBaseReady) {
      this.hemisphereLight.color.copy(this.baseHemiSky);
      this.hemisphereLight.groundColor.copy(this.baseHemiGround);
      this.hemisphereLight.intensity = this.baseHemiIntensity;
    }
    if (this.waterUniforms && this.waterSunBaseReady) {
      const sunColor = this.waterUniforms.sunColor?.value;
      if (sunColor instanceof THREE.Color) sunColor.copy(this.baseWaterSunColor);
    }

    const fog = this.scene.fog as THREE.Fog | null;
    if (fog && this.fogBaseReady) {
      fog.color.copy(this.baseFogColor);
      fog.near = this.baseFogNear;
      fog.far = this.baseFogFar;
    }
    const bg = this.scene.background;
    if (bg instanceof THREE.Color && this.bgBaseReady) {
      bg.copy(this.baseBackground);
    }
  }

  /** Spawn sea and deck splashes at a rate proportional to the rain. */
  private updateSplashes(
    dt: number,
    rain: number,
    gust: number,
    flash: number,
    camX: number,
    camZ: number,
  ): void {
    this.splashes.update(dt, rain, flash);

    if (rain <= 0.05) {
      this.seaSplashTimer = 0;
      this.deckSplashTimer = 0;
      return;
    }

    this.seaSplashTimer -= dt;
    if (this.seaSplashTimer <= 0) {
      this.seaSplashTimer = SPLASH_INTERVAL / rain;
      const angle = this.splashRng() * Math.PI * 2;
      const distance = Math.sqrt(this.splashRng()) * this.splashRadius;
      this.splashes.spawn(
        camX + Math.cos(angle) * distance,
        0.15,
        camZ + Math.sin(angle) * distance,
        gust * 3,
        1.0 + this.splashRng() * 1.1,
        gust * 2,
      );
    }

    this.deckSplashTimer -= dt;
    if (this.deckSplashTimer <= 0) {
      this.deckSplashTimer = DECK_SPLASH_INTERVAL / rain;
      this.spawnDeckSplash(gust);
    }
  }

  /** Splash a raindrop landing on the player ship's deck. */
  private spawnDeckSplash(gust: number): void {
    if (!this.shipGroup) return;
    this.shipGroup.getWorldPosition(this.shipWorldPos);
    this.shipGroup.getWorldQuaternion(this.shipQuat);
    this.shipLocal.set(
      (this.splashRng() * 2 - 1) * 7,
      DECK_HEIGHT + 0.2,
      (this.splashRng() * 2 - 1) * 2.6,
    );
    this.shipWorldOffset.copy(this.shipLocal).applyQuaternion(this.shipQuat);
    this.splashes.spawn(
      this.shipWorldPos.x + this.shipWorldOffset.x,
      this.shipWorldPos.y + this.shipWorldOffset.y,
      this.shipWorldPos.z + this.shipWorldOffset.z,
      gust * 1.5,
      0.8 + this.splashRng() * 0.8,
      gust,
    );
  }

  /** Re-tint and dim the ocean's sun/hemisphere lights as the storm builds. */
  private updateLighting(stormAmount: number, flash: number): void {
    if (this.sunLight) {
      if (!this.sunBaseReady) {
        this.baseSunColor.copy(this.sunLight.color);
        this.baseSunIntensity = this.sunLight.intensity;
        this.sunBaseReady = true;
      }
      this.sunColorScratch.copy(this.baseSunColor).lerp(this.coldSunColor, stormAmount * 0.85);
      let sunIntensity = this.baseSunIntensity * (1 - DIR_DIM_FACTOR * stormAmount);
      if (flash > 0) {
        this.sunColorScratch.lerp(this.flashWhite, flash * 0.6);
        sunIntensity += flash * FLASH_LIGHT_INTENSITY * 0.5;
      }
      this.sunLight.color.copy(this.sunColorScratch);
      this.sunLight.intensity = sunIntensity;
    }

    if (this.hemisphereLight) {
      if (!this.hemiBaseReady) {
        this.baseHemiSky.copy(this.hemisphereLight.color);
        this.baseHemiGround.copy(this.hemisphereLight.groundColor);
        this.baseHemiIntensity = this.hemisphereLight.intensity;
        this.hemiBaseReady = true;
      }
      this.hemiSkyScratch.copy(this.baseHemiSky).lerp(this.coldHemiSky, stormAmount * 0.8);
      this.hemiGroundScratch.copy(this.baseHemiGround).lerp(this.coldHemiGround, stormAmount * 0.8);
      let hemiIntensity = this.baseHemiIntensity * (1 - HEMI_DIM_FACTOR * stormAmount);
      if (flash > 0) {
        this.hemiSkyScratch.lerp(this.flashWhite, flash * 0.5);
        hemiIntensity += flash * FLASH_LIGHT_INTENSITY * 0.25;
      }
      this.hemisphereLight.color.copy(this.hemiSkyScratch);
      this.hemisphereLight.groundColor.copy(this.hemiGroundScratch);
      this.hemisphereLight.intensity = hemiIntensity;
    }
  }

  /** Turn the water's specular sun cold/dim, brightening it only on a flash. */
  private updateWaterSun(stormAmount: number, flash: number): void {
    if (!this.waterUniforms) return;
    const sunColor = this.waterUniforms.sunColor?.value;
    if (!(sunColor instanceof THREE.Color)) return;

    if (!this.waterSunBaseReady) {
      this.baseWaterSunColor.copy(sunColor);
      this.waterSunBaseReady = true;
    }

    this.waterSunScratch.copy(this.baseWaterSunColor).lerp(this.coldWaterSunColor, stormAmount * 0.75);
    if (flash > 0) this.waterSunScratch.lerp(this.flashWhite, flash * 0.7);
    sunColor.copy(this.waterSunScratch);
  }

  /** Pull fog in and deepen its colour as the storm builds, then flash it. */
  private updateFog(fogAmount: number, stormAmount: number, flash: number): void {
    const fog = this.scene.fog as THREE.Fog | null;
    if (!fog) return;

    if (!this.fogBaseReady) {
      this.baseFogNear = fog.near;
      this.baseFogFar = fog.far;
      this.baseFogColor.copy(fog.color);
      this.fogBaseReady = true;
    }

    const density = clamp01(Math.max(fogAmount, stormAmount * 0.85));
    fog.near = this.baseFogNear * (1 - FOG_NEAR_PULL * density);
    fog.far = this.baseFogFar * (1 - FOG_FAR_PULL * density);

    this.fogColor.copy(this.baseFogColor).lerp(this.stormFogColor, density);
    if (flash > 0) this.fogColor.lerp(this.flashWhite, flash * 0.85);
    fog.color.copy(this.fogColor);
  }

  /** Darken the sky background as clouds/storms roll in, then flash it. */
  private updateBackground(stormAmount: number, cloudAmount: number, flash: number): void {
    const bg = this.scene.background;
    if (!(bg instanceof THREE.Color)) return;

    if (!this.bgBaseReady) {
      this.baseBackground.copy(bg);
      this.bgBaseReady = true;
    }

    const overcast = clamp01(Math.max(stormAmount, cloudAmount * 0.85));
    this.bgColor.copy(this.baseBackground).lerp(this.stormBgColor, overcast * 0.9);
    if (flash > 0) this.bgColor.lerp(this.flashWhite, flash * 0.9);
    bg.copy(this.bgColor);
  }
}

function makeNearRainConfig(mobile: boolean): RainLayerConfig {
  return {
    count: mobile ? NEAR_RAIN_COUNT_MOBILE : NEAR_RAIN_COUNT_DESKTOP,
    radius: mobile ? NEAR_RAIN_RADIUS_MOBILE : NEAR_RAIN_RADIUS_DESKTOP,
    minRadius: 0,
    top: NEAR_RAIN_TOP,
    topJitter: NEAR_RAIN_TOP_JITTER,
    floor: NEAR_RAIN_FLOOR,
    fallSpeed: NEAR_RAIN_FALL_SPEED,
    drift: NEAR_RAIN_DRIFT,
    slant: NEAR_RAIN_SLANT,
    width: NEAR_RAIN_WIDTH,
    minLength: NEAR_RAIN_MIN_LENGTH,
    maxLength: NEAR_RAIN_MAX_LENGTH,
    speedMin: NEAR_RAIN_SPEED_MIN,
    speedMax: NEAR_RAIN_SPEED_MAX,
    opacity: mobile ? NEAR_RAIN_OPACITY_MOBILE : NEAR_RAIN_OPACITY_DESKTOP,
    color: NEAR_RAIN_COLOR,
    tiltJitter: NEAR_RAIN_TILT_JITTER,
    widthMin: NEAR_RAIN_WIDTH_MIN,
    widthMax: NEAR_RAIN_WIDTH_MAX,
    brightnessMin: NEAR_RAIN_BRIGHTNESS_MIN,
  };
}

function makeMidRainConfig(mobile: boolean): RainLayerConfig {
  return {
    count: mobile ? MID_RAIN_COUNT_MOBILE : MID_RAIN_COUNT_DESKTOP,
    radius: mobile ? MID_RAIN_RADIUS_MOBILE : MID_RAIN_RADIUS_DESKTOP,
    minRadius: mobile ? NEAR_RAIN_RADIUS_MOBILE : NEAR_RAIN_RADIUS_DESKTOP,
    top: MID_RAIN_TOP,
    topJitter: MID_RAIN_TOP_JITTER,
    floor: MID_RAIN_FLOOR,
    fallSpeed: MID_RAIN_FALL_SPEED,
    drift: MID_RAIN_DRIFT,
    slant: MID_RAIN_SLANT,
    width: MID_RAIN_WIDTH,
    minLength: MID_RAIN_MIN_LENGTH,
    maxLength: MID_RAIN_MAX_LENGTH,
    speedMin: MID_RAIN_SPEED_MIN,
    speedMax: MID_RAIN_SPEED_MAX,
    opacity: mobile ? MID_RAIN_OPACITY_MOBILE : MID_RAIN_OPACITY_DESKTOP,
    color: MID_RAIN_COLOR,
    tiltJitter: MID_RAIN_TILT_JITTER,
    widthMin: MID_RAIN_WIDTH_MIN,
    widthMax: MID_RAIN_WIDTH_MAX,
    brightnessMin: MID_RAIN_BRIGHTNESS_MIN,
  };
}
